import "@/App.css";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Section = ({ children, className = "" }) => (
  <section className={`max-w-[1280px] mx-auto px-5 sm:px-8 ${className}`}>{children}</section>
);

const GoldButton = ({ children, className = "", ...props }) => (
  <button
    className={`bg-gradient-to-b from-[#e8c98a] to-[#c8a266] text-[#1a0f06] font-semibold tracking-wide px-6 py-3 rounded-md hover:from-[#f1d59c] hover:to-[#d4ac6f] transition-all shadow-[0_4px_20px_rgba(212,175,116,0.25)] ${className}`}
    {...props}
  >
    {children}
  </button>
);

const Card = ({ children, className = "" }) => (
  <div className={`rounded-xl border border-[#3a2a14]/70 bg-gradient-to-b from-[#14100a] to-[#0d0905] ${className}`}>
    {children}
  </div>
);

const PackageCard = ({ img, title, sub, price, badge, slug }) => (
  <a href={`/package/${slug}`} data-testid={`all-pkg-card-${slug}`} className="block">
    <Card className="overflow-hidden group cursor-pointer hover:border-[#d4a574]/50 transition h-full">
      <div className="relative h-52 overflow-hidden">
        {img && <img src={img} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0604] via-[#0a0604]/30 to-transparent" />
        {badge && <span className="absolute top-3 right-3 bg-[#f4d089] text-[#1a0f06] text-[10px] font-bold tracking-wider px-2.5 py-1 rounded">{badge}</span>}
      </div>
      <div className="p-5">
        <h4 className="text-white font-display text-lg">{title}</h4>
        <p className="text-xs text-[#a89878] mt-1">{sub}</p>
        <div className="text-[#f4d089] text-sm mt-2">Starting at <b>₹{price}</b></div>
      </div>
    </Card>
  </a>
);

const Packages = () => {
  const [pkgs, setPkgs] = useState([]);
  useEffect(() => {
    axios.get(`${API}/packages`).then((r) => setPkgs(r.data || [])).catch(() => setPkgs([]));
  }, []);

  const all = pkgs.map((p) => ({
    slug: p.slug,
    img: p.image,
    title: p.name,
    sub: p.sub || "",
    price: p.base_price ? Number(p.base_price).toLocaleString("en-IN") : "—",
    badge: p.badge || "",
  }));

  return (
    <div className="min-h-screen bg-[#0a0604] text-[#e8dcc0]" data-testid="all-packages-page">
      <header className="sticky top-0 z-50 backdrop-blur-md bg-[#0a0604]/85 border-b border-[#d4a574]/15">
        <Section className="flex items-center justify-between py-4">
          <a href="/" className="flex items-center" data-testid="cinevibe-logo">
  <div className="w-16 h-16 rounded-full overflow-hidden border border-[#d4a574]/40 flex items-center justify-center bg-[#0a0604]">
    <img src="/images/logo.png" alt="CineVibe logo" className="w-full h-full object-cover" />
  </div>
</a>
          <GoldButton data-testid="all-pkg-header-book" onClick={() => (window.location.href = "/book")} className="text-sm px-5 py-2.5">BOOK NOW</GoldButton>
        </Section>
      </header>

      <Section className="pt-10 pb-6 text-center">
        <span className="inline-block bg-[#f4d089] text-[#1a0f06] text-[11px] font-bold tracking-wider px-3 py-1 rounded">READY PACKAGES</span>
        <h1 className="font-display text-white text-3xl sm:text-5xl mt-4">Plug-and-play celebrations</h1>
        <p className="text-[#bfb393] mt-3 max-w-xl mx-auto text-[15px]">
          Curated packages with decor, dining and the screen — pick one, we handle the rest.
        </p>
      </Section>

      <Section className="pb-16">
        {all.length === 0 ? (
          <div className="text-center text-[#a89878] py-16">No packages published yet.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {all.map((p) => <PackageCard key={p.slug} {...p} />)}
          </div>
        )}
      </Section>

      <div className="border-t border-[#3a2a14]/60 bg-gradient-to-b from-[#0d0905] to-[#14100a]">
        <Section className="py-14 text-center">
          <h3 className="font-display text-2xl sm:text-3xl text-white">Want something custom?</h3>
          <p className="text-[#bfb393] mt-3">Tell us the occasion — we'll build a bespoke private-screen package for you.</p>
          <div className="flex justify-center mt-6">
            <GoldButton onClick={() => (window.location.href = "/book")} data-testid="all-pkg-footer-book" className="text-base tracking-[0.15em] flex items-center gap-2">
              BOOK A PRIVATE SCREEN NOW <ArrowRight size={18} />
            </GoldButton>
          </div>
        </Section>
      </div>

      <footer className="border-t border-[#3a2a14]/60 py-6 text-center text-xs text-[#a89878] leading-relaxed">
        © {new Date().getFullYear()} The CineVibe Private Screens & Café<br />
        BnBBuddy Property Management Services Pvt Ltd · GSTIN: 07AALCB5876H1Z8
      </footer>
    </div>
  );
};

export default Packages;
